import { api, ApiError } from '@/core/lib/apiClient';
import type {
  Capabilities,
  Dashboard,
  DeliveryPreferences,
  MixFeed,
  Page,
  Payloads,
  Resource,
  ResourceKind,
  Visibility,
} from './contracts';

const detail = (kind: ResourceKind, slug: string) => `/${kind}/${encodeURIComponent(slug)}/`;
export interface Draft<K extends ResourceKind> {
  title: string;
  description: string;
  visibility: Visibility;
  data: Payloads[K];
}
/** No cross-account cache. View owners discard stale reads and mutation results. */
export const networks = {
  capabilities: () => api<Capabilities>('/networks/capabilities/'),
  list: <K extends ResourceKind>(kind: K, cursor?: string) =>
    api<Page<K>>(`/${kind}/${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
  get: <K extends ResourceKind>(kind: K, slug: string) => api<Resource<K>>(detail(kind, slug)),
  remove: (r: Resource) =>
    api<void>(detail(r.kind, r.slug), { method: 'DELETE', body: { revision: r.revision } }),
  history: (r: Resource) =>
    api<{ items: { number: number; author: string | null; createdAt: string }[] }>(
      detail(r.kind, r.slug) + 'revisions/',
    ),
  create: <K extends ResourceKind>(kind: K, draft: Draft<K>) =>
    api<Resource<K>>(`/${kind}/`, { method: 'POST', body: draft }),
  update: <K extends ResourceKind>(r: Resource<K>, draft: Draft<K>) =>
    api<Resource<K>>(detail(r.kind, r.slug), {
      method: 'PATCH',
      body: { ...draft, revision: r.revision },
    }),
  action: <K extends ResourceKind>(
    r: Resource<K>,
    action: string,
    values: Record<string, unknown> = {},
  ) =>
    api<Resource<K>>(detail(r.kind, r.slug) + 'actions/', {
      method: 'POST',
      body: { action, revision: r.revision, ...values },
    }),
  feed: (slug: string, mode: string, cursor?: string, snapshot?: string) => {
    const query = new URLSearchParams({ mode });
    if (cursor) query.set('cursor', cursor);
    if (snapshot) query.set('snapshot', snapshot);
    return api<MixFeed>(`${detail('mixes', slug)}feed/?${query}`);
  },
  delivery: () => api<DeliveryPreferences>('/notification-delivery/'),
  saveDelivery: (body: DeliveryPreferences) =>
    api<DeliveryPreferences>('/notification-delivery/', { method: 'PUT', body }),
  dashboard: () => api<Dashboard>('/creator-dashboard/'),
};

export function errorCopy(error: unknown): string {
  const code = error instanceof Error ? error.message : '';
  const known: Record<string, string> = {
    collaboration_changed:
      'The shared draft changed. Reopen it, refresh its details, and review the latest writing before posting.',
    audience_ring_changed:
      'This ring changed. Reopen it and review the latest recipients before posting.',
    audience_ring_empty: 'This ring has no recipients. Review the ring before posting.',
    audience_ring_members_unavailable:
      'A ring recipient is unavailable. Review and save your recipients again.',
    perspective_invitations_pending: 'Resolve pending perspective invitations before attaching.',
    perspective_needs_two_viewpoints: 'Add at least two available viewpoints before attaching.',
    perspective_already_attached:
      'These perspectives are already attached to a post. Choose another draft.',
    collaboration_already_attached:
      'This collaboration is already attached to a post. Open it to propose a published change.',
    revision_conflict:
      'Someone edited this first. Your draft is retained. Reload the latest version before saving again.',
    feature_disabled: 'This feature is not enabled yet. Your draft is retained.',
    permission_denied: 'You no longer have permission for this action.',
    pin_limit: 'You can pin up to five Mixes. Unpin one first.',
    consent_required: 'Every quoted contributor must consent before publishing.',
    collaborators_pending: 'All invited co-authors must accept before publishing.',
    invalid_provider_url: 'Use an HTTPS YouTube or Twitch video/channel link.',
    invitations_restricted:
      'This account does not accept invitations from you. Ask them to review their invitation settings.',
    account_unavailable: 'That account is unavailable. Check the handle and your mute settings.',
    source_unavailable: 'A selected source is unavailable. Check its post/comment ID and access.',
    community_member_required: 'Only an existing community member can be appointed.',
    channel_exists: 'You already have a creator channel. Open it to make changes.',
    edit_window_expired: 'The post’s editing window has ended. Your proposed text is retained.',
  };
  if (code === 'validation' && error instanceof ApiError && error.fields) {
    const messages = Object.entries(error.fields)
      .slice(0, 3)
      .map(
        ([field, value]) =>
          `${field}: ${Array.isArray(value) ? value.filter((v) => typeof v === 'string').join(' ') : typeof value === 'string' ? value : 'Check this value.'}`,
      );
    if (messages.length) return messages.join(' ') + ' Your input is retained.';
  }
  return known[code] ?? 'Could not save this change. Your input is retained; please try again.';
}
